import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Divider,
  IconButton,
  Snackbar,
  useMediaQuery,
  Slider,
  Badge,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupIcon from "@mui/icons-material/Group";
import BikeWorkoutPickerDrawer from './BikeWorkoutPickerDrawer';
import { importGarminActivity } from '../api/garminApi';
import ClubPickerDrawer from './running/ClubPickerDrawer';
import { tokens } from "../theme";
import { useAuth } from "../contexts/AuthContext";
import { downloadUserFile, deleteUserFile, downloadCommunityBikeWorkout } from "../api/client";
import { getUserParameters } from "../services/userParametersService";
import { getMyPendingCount } from "../api/clubApi";

// Unit conversion utilities
export const getUnits = () => localStorage.getItem('units') || 'metric';
export const speedLabel = getUnits() === 'imperial' ? 'mph' : 'km/h';
export const weightLabel = getUnits() === 'imperial' ? 'lb' : 'kg';

// Convert from metric to display units
const convertWeightToDisplay = (weight, units) => units === 'imperial' ? weight * 2.20462 : weight;
const convertSpeedToDisplay = (speed, units) => units === 'imperial' ? speed * 0.621371 : speed;

const FileUpload = ({ onFileUpload, loading }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [clubPickerOpen, setClubPickerOpen] = useState(false);
  const [clubPendingCount, setClubPendingCount] = useState(0);
  const [reusedFileId, setReusedFileId] = useState(null); // Track file ID when reusing from history
  const [description, setDescription] = useState('');
  const [loadingFromHistory, setLoadingFromHistory] = useState(false); // Track if we're loading from history
  const [bikeType, setBikeType] = useState("road");
  const [terrainType, setTerrainType] = useState("paved"); // 'paved', 'gravel', 'mixed'
  const [terrainPavedPercentage, setTerrainPavedPercentage] = useState(100); // 0-100
  const [riderWeight, setRiderWeight] = useState(75); // kg
  const [bikeWeight, setBikeWeight] = useState(8.5); // kg
  const [ttAeroMinSpeed, setTtAeroMinSpeed] = useState(18); // km/h
  const [ttAeroMaxSpeed, setTtAeroMaxSpeed] = useState(50); // km/h
  const [roadDropsMinSpeed, setRoadDropsMinSpeed] = useState(35); // km/h
  const [roadAeroAerobarMinSpeed, setRoadAeroAerobarMinSpeed] = useState(18); // km/h (same as TT aero min speed)
  const [roadAeroAerobarMaxSpeed, setRoadAeroAerobarMaxSpeed] = useState(40); // km/h
  const [roadAeroDropsMinSpeed, setRoadAeroDropsMinSpeed] = useState(40); // km/h
  const [gravelDropsMinSpeed, setGravelDropsMinSpeed] = useState(30); // km/h
  const [airDensity, setAirDensity] = useState(1.225);
  const [rollingResistance, setRollingResistance] = useState(0.004);
  const [cdaTtAero, setCdaTtAero] = useState(0.24);
  const [cdaTtBase, setCdaTtBase] = useState(0.38);
  const [cdaRoadHoods, setCdaRoadHoods] = useState(0.40);
  const [cdaRoadDrops, setCdaRoadDrops] = useState(0.32);
  const [cdaRoadAeroHoods, setCdaRoadAeroHoods] = useState(0.40);
  const [cdaRoadAeroAerobar, setCdaRoadAeroAerobar] = useState(0.30);
  const [cdaRoadAeroDrops, setCdaRoadAeroDrops] = useState(0.32);
  const [cdaGravelHoods, setCdaGravelHoods] = useState(0.42);
  const [cdaGravelDrops, setCdaGravelDrops] = useState(0.34);
  const [error, setError] = useState(null);
  const [showLongWaitMessage, setShowLongWaitMessage] = useState(false);
  const inputRef = useRef(null);
  const isInitialMount = useRef(true);
  const loadingTimerRef = useRef(null);
  const [userParametersLoaded, setUserParametersLoaded] = useState(false);
  const bikeTypeFromHistory = useRef(null); // Track the bike type loaded from history

  // Units state to make component reactive to unit changes
  const [units, setUnits] = useState(() => getUnits());

  // Function to calculate rolling resistance based on bike type and terrain
  const calculateRollingResistance = (bikeType, terrainType, pavedPercentage) => {
    // For road_aero and tt bikes: always 0.004 (paved roads only)
    if (bikeType === 'road_aero' || bikeType === 'tt') {
      return 0.004;
    }

    // For road bike: terrain-aware like gravel but with different values
    if (bikeType === 'road') {
      if (terrainType === 'paved') {
        return 0.004;
      } else if (terrainType === 'gravel') {
        return 0.010;  // Road bike on pure gravel
      } else if (terrainType === 'mixed') {
        // Weighted average: (paved% * 0.004) + (gravel% * 0.010)
        const pavedFraction = pavedPercentage / 100;
        const gravelFraction = 1 - pavedFraction;
        return (pavedFraction * 0.004) + (gravelFraction * 0.010);
      }
    }

    // For gravel bike:
    if (bikeType === 'gravel') {
      if (terrainType === 'paved') {
        return 0.005;
      } else if (terrainType === 'gravel') {
        return 0.012;
      } else if (terrainType === 'mixed') {
        // Weighted average: (paved% * 0.005) + (gravel% * 0.012)
        const pavedFraction = pavedPercentage / 100;
        const gravelFraction = 1 - pavedFraction;
        return (pavedFraction * 0.005) + (gravelFraction * 0.012);
      }
    }

    return 0.004; // fallback
  };

  // Auto-update rolling resistance when bike type, terrain type, or paved percentage changes
  useEffect(() => {
    const newCrr = calculateRollingResistance(bikeType, terrainType, terrainPavedPercentage);
    setRollingResistance(parseFloat(newCrr.toFixed(4)));
  }, [bikeType, terrainType, terrainPavedPercentage]);

  // Load pending club requests count for notification badge
  useEffect(() => {
    if (!isAuthenticated) return;
    getMyPendingCount()
      .then((data) => setClubPendingCount(data.pending_count || 0))
      .catch(() => {});
  }, [isAuthenticated]);

  // Force terrain to 'paved' for road_aero and tt bikes only
  useEffect(() => {
    if (bikeType === 'road_aero' || bikeType === 'tt') {
      if (terrainType !== 'paved') {
        setTerrainType('paved');
        setTerrainPavedPercentage(100);
      }
    }
  }, [bikeType, terrainType]);

  // Listen for unit changes
  useEffect(() => {
    const handleUnitsChange = (e) => {
      setUnits(e.detail || getUnits());
    };

    window.addEventListener('unitsChanged', handleUnitsChange);

    return () => {
      window.removeEventListener('unitsChanged', handleUnitsChange);
    };
  }, []);

  // Load user parameters on component mount
  useEffect(() => {
    const loadUserParameters = async () => {
      try {
        const params = await getUserParameters(isAuthenticated);

        // Set rider weight (common parameter)
        if (params.rider_weight) setRiderWeight(params.rider_weight);

        // Set road bike parameters
        if (params.road_bike_weight) setBikeWeight(prev => bikeType === 'road' ? params.road_bike_weight : prev);
        if (params.road_drops_min_speed) setRoadDropsMinSpeed(params.road_drops_min_speed);
        if (params.road_cda_hoods) setCdaRoadHoods(params.road_cda_hoods);
        if (params.road_cda_drops) setCdaRoadDrops(params.road_cda_drops);

        // Set TT bike parameters
        if (params.tt_bike_weight && bikeType === 'tt') setBikeWeight(params.tt_bike_weight);
        if (params.tt_aero_min_speed) setTtAeroMinSpeed(params.tt_aero_min_speed);
        if (params.tt_aero_max_speed) setTtAeroMaxSpeed(params.tt_aero_max_speed);
        if (params.tt_cda_aero) setCdaTtAero(params.tt_cda_aero);
        if (params.tt_cda_base) setCdaTtBase(params.tt_cda_base);

        // Set advanced physics settings
        if (params.air_density) setAirDensity(params.air_density);
        if (params.rolling_resistance) setRollingResistance(params.rolling_resistance);

        setUserParametersLoaded(true);
        console.log('Loaded user parameters:', params);
      } catch (error) {
        console.error('Error loading user parameters:', error);
        setUserParametersLoaded(true); // Still mark as loaded to prevent infinite retry
      }
    };

    if (!userParametersLoaded) {
      loadUserParameters();
    }
  }, [isAuthenticated, userParametersLoaded]);

  // Update bike weight when bike type changes (using user's saved parameters)
  useEffect(() => {
    // Check if user manually changed bike type (different from history)
    const userChangedBikeType = bikeTypeFromHistory.current !== null && bikeType !== bikeTypeFromHistory.current;
    const isManualBikeTypeChange = (!loadingFromHistory || userChangedBikeType) &&
                                    userParametersLoaded &&
                                    (bikeTypeFromHistory.current === null || userChangedBikeType);

    if (isManualBikeTypeChange) {
      const loadBikeTypeParameters = async () => {
        const params = await getUserParameters(isAuthenticated);

        // Update bike weight based on bike type
        if (bikeType === 'tt' && params.tt_bike_weight) {
          console.log('FileUpload: Setting TT bike weight from user params:', params.tt_bike_weight);
          setBikeWeight(params.tt_bike_weight);
        } else if (bikeType === 'road' && params.road_bike_weight) {
          console.log('FileUpload: Setting road bike weight from user params:', params.road_bike_weight);
          setBikeWeight(params.road_bike_weight);
        }

        // Clear the history bike type reference and loading flag after manual change
        if (bikeTypeFromHistory.current !== null) {
          bikeTypeFromHistory.current = null;
          setLoadingFromHistory(false);
        }
      };

      loadBikeTypeParameters();
    } else if (loadingFromHistory && !userChangedBikeType) {
      console.log('Skipping bike weight update - loading from history');
    }
  }, [bikeType, loadingFromHistory, userParametersLoaded, isAuthenticated]);

  // Dynamic labels based on current units
  const speedLabel = units === 'imperial' ? 'mph' : 'km/h';
  const weightLabel = units === 'imperial' ? 'lb' : 'kg';

  // Show friendly message after 5 seconds of loading
  useEffect(() => {
    if (loading) {
      loadingTimerRef.current = setTimeout(() => {
        setShowLongWaitMessage(true);
      }, 5000);
    } else {
      // Clear timer and hide message when loading stops
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setShowLongWaitMessage(false);
    }

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [loading]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    setError(null);
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.fit')) {
      setError('Please select a valid FIT file');
      return;
    }
    setSelectedFile(file);
    setReusedFileId(null); // Clear reused file ID when uploading new file
setLoadingFromHistory(false); // Reset loading from history when new file is uploaded
    bikeTypeFromHistory.current = null; // Clear history bike type reference
  };

  const handleSavedFileSelect = async (fileInfo) => {
    try {
      setError(null);
      setLoadingFromHistory(true); // Prevent parameter reset

      // Download the file
      const blob = await downloadUserFile('fit', fileInfo.id);
      
      // Create a File object from the blob
      const file = new File([blob], fileInfo.filename, { type: 'application/octet-stream' });
      
      setSelectedFile(file);
      setReusedFileId(fileInfo.id); // Track the file ID for updating
      
      console.log('Loading from history - bike_type:', fileInfo.bike_type, 'bike_weight:', fileInfo.bike_weight);

      // Set bike_type FIRST to avoid bike weight being overridden
      if (fileInfo.bike_type !== null && fileInfo.bike_type !== undefined) {
        setBikeType(fileInfo.bike_type);
        // Remember the bike type from history so we can detect manual changes
        bikeTypeFromHistory.current = fileInfo.bike_type;
      }

      // Load saved parameters - bike_weight MUST be set AFTER bike_type to override the default
      if (fileInfo.rider_weight !== null && fileInfo.rider_weight !== undefined) setRiderWeight(fileInfo.rider_weight);
      if (fileInfo.tt_aero_min_speed !== null && fileInfo.tt_aero_min_speed !== undefined) setTtAeroMinSpeed(fileInfo.tt_aero_min_speed);
      if (fileInfo.tt_aero_max_speed !== null && fileInfo.tt_aero_max_speed !== undefined) setTtAeroMaxSpeed(fileInfo.tt_aero_max_speed);
      if (fileInfo.road_drops_min_speed !== null && fileInfo.road_drops_min_speed !== undefined) setRoadDropsMinSpeed(fileInfo.road_drops_min_speed);
      if (fileInfo.air_density !== null && fileInfo.air_density !== undefined) setAirDensity(fileInfo.air_density);
      if (fileInfo.rolling_resistance !== null && fileInfo.rolling_resistance !== undefined) setRollingResistance(fileInfo.rolling_resistance);
      if (fileInfo.cda_tt_aero !== null && fileInfo.cda_tt_aero !== undefined) setCdaTtAero(fileInfo.cda_tt_aero);
      if (fileInfo.cda_tt_base !== null && fileInfo.cda_tt_base !== undefined) setCdaTtBase(fileInfo.cda_tt_base);
      if (fileInfo.cda_road_hoods !== null && fileInfo.cda_road_hoods !== undefined) setCdaRoadHoods(fileInfo.cda_road_hoods);
      if (fileInfo.cda_road_drops !== null && fileInfo.cda_road_drops !== undefined) setCdaRoadDrops(fileInfo.cda_road_drops);
      if (fileInfo.cda_road_aero_hoods !== null && fileInfo.cda_road_aero_hoods !== undefined) setCdaRoadAeroHoods(fileInfo.cda_road_aero_hoods);
      if (fileInfo.cda_road_aero_aerobar !== null && fileInfo.cda_road_aero_aerobar !== undefined) setCdaRoadAeroAerobar(fileInfo.cda_road_aero_aerobar);
      if (fileInfo.cda_road_aero_drops !== null && fileInfo.cda_road_aero_drops !== undefined) setCdaRoadAeroDrops(fileInfo.cda_road_aero_drops);
      if (fileInfo.cda_gravel_hoods !== null && fileInfo.cda_gravel_hoods !== undefined) setCdaGravelHoods(fileInfo.cda_gravel_hoods);
      if (fileInfo.cda_gravel_drops !== null && fileInfo.cda_gravel_drops !== undefined) setCdaGravelDrops(fileInfo.cda_gravel_drops);
      if (fileInfo.terrain_type !== null && fileInfo.terrain_type !== undefined) setTerrainType(fileInfo.terrain_type);
      if (fileInfo.terrain_paved_percentage !== null && fileInfo.terrain_paved_percentage !== undefined) setTerrainPavedPercentage(fileInfo.terrain_paved_percentage);
      setDescription(fileInfo.description || '');

      // Set bike_weight LAST after a small delay to ensure it overrides any useEffect
      setTimeout(() => {
        if (fileInfo.bike_weight !== null && fileInfo.bike_weight !== undefined) {
          console.log('Setting bike_weight from history:', fileInfo.bike_weight);
          setBikeWeight(fileInfo.bike_weight);
        }
        // Do NOT reset loadingFromHistory here - it will trigger the useEffect that loads user params
        // loadingFromHistory is only reset when user uploads a new file (in handleFile)
      }, 150);

    } catch (err) {
      setError('Failed to load saved file');
      console.error(err);
      setLoadingFromHistory(false);
    }
  };

  const handleBikePickerSelect = async (source) => {
    setPickerOpen(false);
    if (source.type === 'myfile' && source.record) {
      await handleSavedFileSelect(source.record);
    } else if (source.type === 'garmin' && source.activityId) {
      // Importing promotes the staged activity into the user's files; from
      // there it behaves exactly like any other saved file.
      try {
        const result = await importGarminActivity(source.activityId);
        await handleSavedFileSelect({
          id: result.file_id,
          filename: result.filename || `garmin_${source.activityId}.fit`,
        });
      } catch (err) {
        console.error('Failed to import Garmin activity:', err);
        setError(err?.response?.data?.detail || 'Failed to import that Garmin activity');
      }
    } else if (source.type === 'community' && source.sharedId) {
      try {
        const blob = await downloadCommunityBikeWorkout(source.sharedId);
        const filename = source.record?.filename || `community_${source.sharedId}.fit`;
        const file = new File([blob], filename, { type: 'application/octet-stream' });
        setSelectedFile(file);
        setReusedFileId(null);
        setError(null);
      } catch (err) {
        console.error('Failed to download community file:', err);
        setError('Failed to download community file');
      }
    } else if (source.type === 'clubFile' && source.clubId && source.clubFileId) {
      try {
        const { analyzeClubBikeFile } = await import('../api/clubApi');
        const workoutData = await analyzeClubBikeFile(source.clubId, source.clubFileId);
        setError(null);
        navigate('/analysis', {
          state: {
            workoutData,
            clubContext: {
              clubId: source.clubId,
              clubFileId: source.clubFileId,
              filename: source.record?.filename,
            },
          },
        });
      } catch (err) {
        console.error('Failed to analyze club file:', err);
        setError(t('failedToLoadClubFile', 'Failed to load club file'));
      }
    }
  };

  const handleUpload = async () => {
    if (selectedFile) {
      try {
        // Values are already stored in metric units
        const params = {
          bikeType,
          riderWeight,
          bikeWeight,
          ttAeroMinSpeed,
          ttAeroMaxSpeed,
          roadDropsMinSpeed,
          roadAeroAerobarMinSpeed,
          roadAeroAerobarMaxSpeed: roadAeroDropsMinSpeed,  // Use drops min speed as aerobar max speed
          roadAeroDropsMinSpeed,
          gravelDropsMinSpeed,
          whatIfBikeType: bikeType,
          whatIfRiderWeight: riderWeight,
          whatIfBikeWeight: bikeWeight,
          whatIfTtAeroMinSpeed: ttAeroMinSpeed,
          whatIfTtAeroMaxSpeed: ttAeroMaxSpeed,
          whatIfRoadDropsMinSpeed: roadDropsMinSpeed,
          whatIfRoadAeroAerobarMinSpeed: roadAeroAerobarMinSpeed,
          whatIfRoadAeroAerobarMaxSpeed: roadAeroDropsMinSpeed,  // Use drops min speed as aerobar max speed
          whatIfRoadAeroDropsMinSpeed: roadAeroDropsMinSpeed,
          whatIfGravelDropsMinSpeed: gravelDropsMinSpeed,
          airDensity,
          rollingResistance,
          cdaTtAero,
          cdaTtBase,
          cdaRoadHoods,
          cdaRoadDrops,
          cdaRoadAeroHoods,
          cdaRoadAeroAerobar,
          cdaRoadAeroDrops,
          cdaGravelHoods,
          cdaGravelDrops,
          terrainType,
          terrainPavedPercentage,
          reusedFileId,
          description: description || null,
        };


        await onFileUpload(selectedFile, params);
        setSelectedFile(null);
        setReusedFileId(null); // Clear after upload
        setBikeType("road");
        setTerrainType("paved");
        setTerrainPavedPercentage(100);
        // Reset to default values in metric units
        setRiderWeight(75);
        setBikeWeight(8.5);
        setTtAeroMinSpeed(18);
        setTtAeroMaxSpeed(50);
        setRoadDropsMinSpeed(35);
        setRoadAeroAerobarMinSpeed(18);
        setRoadAeroAerobarMaxSpeed(40);
        setRoadAeroDropsMinSpeed(40);
        setGravelDropsMinSpeed(30);
        setAirDensity(1.225);
        setRollingResistance(0.004);
        setCdaTtAero(0.24);
        setCdaTtBase(0.38);
        setCdaRoadHoods(0.40);
        setCdaRoadDrops(0.32);
        setCdaRoadAeroHoods(0.40);
        setCdaRoadAeroAerobar(0.30);
        setCdaRoadAeroDrops(0.32);
        setCdaGravelHoods(0.42);
        setCdaGravelDrops(0.34);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      } catch (err) {
        setError(err.message || 'Failed to upload file');
      }
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 3 },
          backgroundColor: colors.grey[800],
          borderRadius: '12px',
        }}
      >
        {/* ── Drop zone ─────────────────────────────────────────────── */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: `2px dashed ${dragActive ? colors.blueAccent[400] : selectedFile ? colors.greenAccent[700] : colors.grey[700]}`,
            borderRadius: '10px',
            p: { xs: 2, md: 2.5 },
            textAlign: 'center',
            backgroundColor: dragActive
              ? colors.blueAccent[900]
              : selectedFile
              ? `${colors.greenAccent[900]}55`
              : colors.grey[900],
            cursor: 'pointer',
            transition: 'all 0.25s ease',
          }}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".fit,.FIT"
            onChange={handleChange}
            style={{ display: 'none' }}
          />
          {selectedFile ? (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              <CheckCircleIcon sx={{ color: colors.greenAccent[400], fontSize: 28 }} />
              <Box textAlign="left">
                <Typography variant="body1" color={colors.greenAccent[300]} fontWeight={600} noWrap>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color={colors.grey[400]}>
                  {`${(selectedFile.size / 1024).toFixed(0)} KB · ${t('clickToChange', 'click to change')}`}
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 36, color: colors.blueAccent[400], mb: 1 }} />
              <Typography variant="body1" color={colors.grey[200]} mb={0.25}>
                {t('dropFitFile', 'Drop your bike FIT file here or click to browse')}
              </Typography>
              <Typography variant="caption" color={colors.grey[500]}>
                {t('acceptedFormats', 'Accepted formats: .fit')}
              </Typography>
            </>
          )}
        </Box>

        {/* ── Action buttons ─────────────────────────────────────────── */}
        <Box display="flex" alignItems="center" gap={1.5} mt={1.5} mb={selectedFile ? 2.5 : 0} flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            startIcon={<HistoryIcon />}
            onClick={(e) => {
              e.stopPropagation();
              if (!isAuthenticated) { setError(t('pleaseLoginViewFiles', 'Please login to view your saved files')); return; }
              setPickerOpen(true);
            }}
            disabled={!isAuthenticated}
            sx={{
              color: colors.blueAccent[400],
              borderColor: colors.blueAccent[700],
              textTransform: 'none',
              '&:hover': { borderColor: colors.blueAccent[400], backgroundColor: `${colors.blueAccent[900]}80` },
            }}
          >
            {t('myBikingFiles', 'My Biking Files')}
          </Button>

          <Badge
            badgeContent={clubPendingCount}
            color="error"
            invisible={!isAuthenticated || clubPendingCount === 0}
            sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem' } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<GroupIcon />}
              onClick={(e) => {
                e.stopPropagation();
                if (!isAuthenticated) { setError(t('pleaseLoginViewClubs', 'Please login to view your clubs')); return; }
                setClubPickerOpen(true);
              }}
              disabled={!isAuthenticated}
              sx={{
                color: colors.blueAccent[400],
                borderColor: colors.blueAccent[700],
                textTransform: 'none',
                '&:hover': { borderColor: colors.blueAccent[400], backgroundColor: `${colors.blueAccent[900]}80` },
              }}
            >
              {t('myClubs', 'My Clubs')}
            </Button>
          </Badge>

          {!isAuthenticated && (
            <Typography variant="caption" color={colors.grey[500]}>
              {t('loginRequired', 'Login to access saved files')}
            </Typography>
          )}

          {!selectedFile && (
            <Button
              variant="outlined"
              size="small"
              onClick={async () => {
                try {
                  const response = await fetch(process.env.PUBLIC_URL + '/data/IMVichy.7h3m.fit');
                  const blob = await response.blob();
                  const file = new File([blob], 'IMVichy.7h3m.fit', { type: 'application/octet-stream' });
                  setSelectedFile(file);
                  setBikeType('tt');
                  setBikeWeight(10);
                  setError(null);
                } catch (err) {
                  console.error('Failed to load example file:', err);
                  setError('Failed to load example file');
                }
              }}
              sx={{
                color: colors.greenAccent[400],
                borderColor: colors.greenAccent[700],
                textTransform: 'none',
                '&:hover': { borderColor: colors.greenAccent[400], backgroundColor: `${colors.greenAccent[900]}80` },
              }}
            >
              {t('useExampleFitFile', 'Try Sample Ride (IM Vichy)')}
            </Button>
          )}
        </Box>

        {selectedFile && (
          <>
            <Divider sx={{ borderColor: colors.grey[700], mb: 2.5 }} />

            {/* Description field */}
            <Box mb={2.5}>
              <TextField
                label={t('fileDescription', 'Description (optional)')}
                size="small"
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                inputProps={{ maxLength: 200 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: colors.grey[700] },
                    '&:hover fieldset': { borderColor: colors.grey[500] },
                    '&.Mui-focused fieldset': { borderColor: colors.blueAccent[400] },
                    color: colors.grey[100],
                  },
                  '& .MuiInputLabel-root': { color: colors.grey[400], '&.Mui-focused': { color: colors.blueAccent[400] } },
                  '& .MuiInputBase-input': { color: colors.grey[100] },
                }}
              />
            </Box>

            <Box>
              <Typography variant="h6" color={colors.grey[100]} mb={2}>
                {t('actualRideConfig', 'Actual Ride Configuration')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: colors.grey[300] }}>{t('bikeTypeUsed', 'Bike Type Used')}</InputLabel>
                  <Select
                    value={bikeType}
                    onChange={(e) => setBikeType(e.target.value)}
                    label="Bike Type Used"
                    sx={{
                      color: colors.grey[100],
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.grey[600],
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.blueAccent[500],
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.blueAccent[500],
                      },
                      '.MuiSvgIcon-root': {
                        color: colors.grey[300],
                      }
                    }}
                  >
                    <MenuItem value="road">{t('roadBike', 'Road Bike')}</MenuItem>
                    <MenuItem value="road_aero">{t('roadBikeAero', 'Road Bike with Aerobars')}</MenuItem>
                    <MenuItem value="tt">{t('timeTrialBike', 'Time Trial Bike')}</MenuItem>
                    <MenuItem value="gravel">{t('gravelBike', 'Gravel Bike')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={`${t('riderWeight', 'Rider Weight')} (${weightLabel})`}
                  value={Math.round(convertWeightToDisplay(riderWeight, units) * 10) / 10}
                  onChange={(e) => setRiderWeight(units === 'imperial' ? parseFloat(e.target.value) / 2.20462 : parseFloat(e.target.value))}
                  inputProps={{ step: "0.5", min: units === 'imperial' ? "80" : "40", max: units === 'imperial' ? "330" : "150" }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: colors.grey[100],
                      '& fieldset': {
                        borderColor: colors.grey[600],
                      },
                      '&:hover fieldset': {
                        borderColor: colors.blueAccent[500],
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.blueAccent[500],
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: colors.grey[300],
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={`${t('bikeWeight', 'Bike Weight')} (${weightLabel})`}
                  value={Math.round(convertWeightToDisplay(bikeWeight, units) * 10) / 10}
                  onChange={(e) => setBikeWeight(units === 'imperial' ? parseFloat(e.target.value) / 2.20462 : parseFloat(e.target.value))}
                  inputProps={{ step: "0.1", min: units === 'imperial' ? "11" : "5", max: units === 'imperial' ? "44" : "20" }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: colors.grey[100],
                      '& fieldset': {
                        borderColor: colors.grey[600],
                      },
                      '&:hover fieldset': {
                        borderColor: colors.blueAccent[500],
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.blueAccent[500],
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: colors.grey[300],
                    },
                  }}
                />
              </Grid>
              {bikeType === 'tt' && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={`${t('aeroMinSpeed', 'Aero Position Min Speed')} (${speedLabel})`}
                      value={Math.round(convertSpeedToDisplay(ttAeroMinSpeed, units))}
                      onChange={(e) => setTtAeroMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                      inputProps={{ step: "1", min: units === 'imperial' ? "6" : "10", max: units === 'imperial' ? "19" : "30" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': {
                            borderColor: colors.grey[600],
                          },
                          '&:hover fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: colors.grey[300],
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={`${t('aeroMaxSpeed', 'Aero Position Max Speed')} (${speedLabel})`}
                      value={Math.round(convertSpeedToDisplay(ttAeroMaxSpeed, units))}
                      onChange={(e) => setTtAeroMaxSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                      inputProps={{ step: "1", min: units === 'imperial' ? "19" : "30", max: units === 'imperial' ? "44" : "70" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': {
                            borderColor: colors.grey[600],
                          },
                          '&:hover fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: colors.grey[300],
                        },
                      }}
                    />
                  </Grid>
                </>
              )}
              {bikeType === 'road_aero' && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={`${t('aerobarMinSpeed', 'Aerobar Min Speed')} (${speedLabel})`}
                      value={Math.round(convertSpeedToDisplay(roadAeroAerobarMinSpeed, units))}
                      onChange={(e) => setRoadAeroAerobarMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                      inputProps={{ step: "1", min: units === 'imperial' ? "6" : "10", max: units === 'imperial' ? "31" : "50" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': {
                            borderColor: colors.grey[600],
                          },
                          '&:hover fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: colors.grey[300],
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={`${t('dropsMinSpeed', 'Drops Position Min Speed')} (${speedLabel})`}
                      value={Math.round(convertSpeedToDisplay(roadAeroDropsMinSpeed, units))}
                      onChange={(e) => setRoadAeroDropsMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                      inputProps={{ step: "1", min: units === 'imperial' ? "19" : "30", max: units === 'imperial' ? "50" : "80" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': {
                            borderColor: colors.grey[600],
                          },
                          '&:hover fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.blueAccent[500],
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: colors.grey[300],
                        },
                      }}
                    />
                  </Grid>
                </>
              )}
              {bikeType === 'road' && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={`${t('dropsMinSpeed', 'Drops Position Min Speed')} (${speedLabel})`}
                    value={Math.round(convertSpeedToDisplay(roadDropsMinSpeed, units))}
                    onChange={(e) => setRoadDropsMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                    inputProps={{ step: "1", min: units === 'imperial' ? "16" : "25", max: units === 'imperial' ? "31" : "50" }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: colors.grey[100],
                        '& fieldset': {
                          borderColor: colors.grey[600],
                        },
                        '&:hover fieldset': {
                          borderColor: colors.blueAccent[500],
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.blueAccent[500],
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: colors.grey[300],
                      },
                    }}
                  />
                </Grid>
              )}
              {bikeType === 'gravel' && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={`${t('dropsMinSpeed', 'Drops Position Min Speed')} (${speedLabel})`}
                    value={Math.round(convertSpeedToDisplay(gravelDropsMinSpeed, units))}
                    onChange={(e) => setGravelDropsMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                    inputProps={{ step: "1", min: units === 'imperial' ? "16" : "25", max: units === 'imperial' ? "31" : "50" }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: colors.grey[100],
                        '& fieldset': {
                          borderColor: colors.grey[600],
                        },
                        '&:hover fieldset': {
                          borderColor: colors.blueAccent[500],
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.blueAccent[500],
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: colors.grey[300],
                      },
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" color={colors.grey[100]} mb={2}>
              {t('terrainAndAdvancedSettings', 'Terrain & Advanced Settings')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth disabled={bikeType === 'road_aero' || bikeType === 'tt'}>
                  <InputLabel sx={{ color: colors.grey[300] }}>{t('terrainType', 'Terrain Type')}</InputLabel>
                  <Select
                    value={terrainType}
                    onChange={(e) => {
                      setTerrainType(e.target.value);
                      // Set default percentages based on terrain type
                      if (e.target.value === 'paved') setTerrainPavedPercentage(100);
                      else if (e.target.value === 'gravel') setTerrainPavedPercentage(0);
                      else setTerrainPavedPercentage(50); // mixed
                    }}
                    label="Terrain Type"
                    sx={{
                      color: colors.grey[100],
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.grey[600],
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.blueAccent[500],
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.blueAccent[500],
                      },
                      '.MuiSvgIcon-root': {
                        color: colors.grey[300],
                      }
                    }}
                  >
                    <MenuItem value="paved">{t('terrainPaved', 'Paved (100%)')}</MenuItem>
                    <MenuItem value="mixed">{t('terrainMixed', 'Mixed (Custom %)')}</MenuItem>
                    <MenuItem value="gravel">{t('terrainGravel', 'Gravel (0% paved)')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('rollingResistance', 'Rolling Resistance')}
                  value={rollingResistance}
                  onChange={(e) => setRollingResistance(parseFloat(e.target.value))}
                  inputProps={{ step: "0.0001", min: "0.002", max: "0.015" }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: colors.grey[100],
                      '& fieldset': { borderColor: colors.grey[600] },
                      '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                      '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
                    },
                    '& .MuiInputLabel-root': { color: colors.grey[300] }
                  }}
                />
              </Grid>
              {terrainType === 'mixed' && (
                <Grid size={{ xs: 12 }}>
                  <Box px={2}>
                    <Typography variant="body2" color={colors.grey[300]} mb={1}>
                      {t('pavedPercentage', 'Paved Surface Percentage')}: {terrainPavedPercentage}%
                    </Typography>
                    <Slider
                      value={terrainPavedPercentage}
                      onChange={(e, value) => setTerrainPavedPercentage(value)}
                      min={0}
                      max={100}
                      step={5}
                      marks={[
                        { value: 0, label: '0%' },
                        { value: 25, label: '25%' },
                        { value: 50, label: '50%' },
                        { value: 75, label: '75%' },
                        { value: 100, label: '100%' }
                      ]}
                      sx={{
                        color: colors.blueAccent[500],
                        '& .MuiSlider-markLabel': {
                          color: colors.grey[400],
                        },
                        '& .MuiSlider-mark': {
                          backgroundColor: colors.grey[600],
                        }
                      }}
                    />
                  </Box>
                </Grid>
              )}
              {bikeType === 'tt' && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('cdaTtAero', 'CdA TT Aero')}
                      value={cdaTtAero}
                      onChange={(e) => setCdaTtAero(parseFloat(e.target.value))}
                      inputProps={{ step: "0.01", min: "0.15", max: "0.30" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': { borderColor: colors.grey[600] },
                          '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                          '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
                        },
                        '& .MuiInputLabel-root': { color: colors.grey[300] }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('cdaTtBase', 'CdA TT Base')}
                      value={cdaTtBase}
                      onChange={(e) => setCdaTtBase(parseFloat(e.target.value))}
                      inputProps={{ step: "0.01", min: "0.20", max: "0.40" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': { borderColor: colors.grey[600] },
                          '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                          '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
                        },
                        '& .MuiInputLabel-root': { color: colors.grey[300] }
                      }}
                    />
                  </Grid>
                </>
              )}
              {bikeType === 'road' && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('cdaRoadHoods', 'CdA Road Hoods')}
                      value={cdaRoadHoods}
                      onChange={(e) => setCdaRoadHoods(parseFloat(e.target.value))}
                      inputProps={{ step: "0.01", min: "0.30", max: "0.50" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': { borderColor: colors.grey[600] },
                          '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                          '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
                        },
                        '& .MuiInputLabel-root': { color: colors.grey[300] }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('cdaRoadDrops', 'CdA Road Drops')}
                      value={cdaRoadDrops}
                      onChange={(e) => setCdaRoadDrops(parseFloat(e.target.value))}
                      inputProps={{ step: "0.01", min: "0.25", max: "0.40" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': { borderColor: colors.grey[600] },
                          '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                          '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
                        },
                        '& .MuiInputLabel-root': { color: colors.grey[300] }
                      }}
                    />
                  </Grid>
                </>
              )}
              {bikeType === 'road_aero' && (
                <>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('cdaRoadAeroHoods', 'CdA Road Aero Hoods')}
                      value={cdaRoadAeroHoods}
                      onChange={(e) => setCdaRoadAeroHoods(parseFloat(e.target.value))}
                      inputProps={{ step: "0.01", min: "0.30", max: "0.50" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': { borderColor: colors.grey[600] },
                          '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                          '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
                        },
                        '& .MuiInputLabel-root': { color: colors.grey[300] }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('cdaRoadAeroAerobar', 'CdA Road Aero Aerobar')}
                      value={cdaRoadAeroAerobar}
                      onChange={(e) => setCdaRoadAeroAerobar(parseFloat(e.target.value))}
                      inputProps={{ step: "0.01", min: "0.20", max: "0.40" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': { borderColor: colors.grey[600] },
                          '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                          '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
                        },
                        '& .MuiInputLabel-root': { color: colors.grey[300] }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('cdaRoadAeroDrops', 'CdA Road Aero Drops')}
                      value={cdaRoadAeroDrops}
                      onChange={(e) => setCdaRoadAeroDrops(parseFloat(e.target.value))}
                      inputProps={{ step: "0.01", min: "0.25", max: "0.40" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': { borderColor: colors.grey[600] },
                          '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                          '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
                        },
                        '& .MuiInputLabel-root': { color: colors.grey[300] }
                      }}
                    />
                  </Grid>
                </>
              )}
              {bikeType === 'gravel' && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('cdaGravelHoods', 'CdA Gravel Hoods')}
                      value={cdaGravelHoods}
                      onChange={(e) => setCdaGravelHoods(parseFloat(e.target.value))}
                      inputProps={{ step: "0.01", min: "0.35", max: "0.55" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': { borderColor: colors.grey[600] },
                          '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                          '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
                        },
                        '& .MuiInputLabel-root': { color: colors.grey[300] }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('cdaGravelDrops', 'CdA Gravel Drops')}
                      value={cdaGravelDrops}
                      onChange={(e) => setCdaGravelDrops(parseFloat(e.target.value))}
                      inputProps={{ step: "0.01", min: "0.25", max: "0.45" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.grey[100],
                          '& fieldset': { borderColor: colors.grey[600] },
                          '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                          '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
                        },
                        '& .MuiInputLabel-root': { color: colors.grey[300] }
                      }}
                    />
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('airDensity', 'Air Density')}
                  value={airDensity}
                  onChange={(e) => setAirDensity(parseFloat(e.target.value))}
                  inputProps={{ step: "0.001", min: "1.0", max: "1.5" }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: colors.grey[100],
                      '& fieldset': { borderColor: colors.grey[600] },
                      '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                      '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
                    },
                    '& .MuiInputLabel-root': { color: colors.grey[300] }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

            <Box display="flex" justifyContent="center" mt={3}>
              <Button
                variant="contained"
                size="large"
                onClick={handleUpload}
                disabled={loading}
                sx={{
                  px: 4,
                  backgroundColor: colors.blueAccent[600],
                  '&:hover': { backgroundColor: colors.blueAccent[500] },
                  '&:disabled': { backgroundColor: colors.grey[700], color: colors.grey[500] },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={18} sx={{ mr: 1, color: colors.grey[300] }} />
                    {t('analyzing', 'Analyzing...')}
                  </>
                ) : (
                  t('analyzeWorkout', 'Analyze Bike Workout')
                )}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Long wait message snackbar */}
      <Snackbar
        open={showLongWaitMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={t('analysisLongWait', 'Still working on it... Longer workouts may take a bit more time to process.')}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: '14px'
          }
        }}
      />

      <BikeWorkoutPickerDrawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleBikePickerSelect}
      />
      <ClubPickerDrawer
        open={clubPickerOpen}
        onClose={() => setClubPickerOpen(false)}
      />
    </Box>
  );
};

export default FileUpload;
